import captureProjectThumbnail  from '@/helpers/projectThumbnail';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import fileStore                 from '@/stores/fileStore';
import React                     from 'react';
import { isAutoThumbnail }       from '@/helpers/projectThumbnail';
import { projectImage }          from '@/helpers/projectThumbnail';
import { reaction }              from 'mobx';
import { TokaMapInstance }       from '@tokamap/core';

/** Silence attendu après le dernier enregistrement : remis à zéro par chacun, une salve d'édition ne
 * donne donc qu'une capture. */
const QUIET_DELAY_MS = 10_000;

/** Laisse arriver les dates de dernière modification et la liste des fichiers, sur lesquelles la
 * décision s'appuie. */
const INITIAL_DELAY_MS = 5_000;

/** Plancher entre deux captures d'une même session : chaque envoi retire le fichier précédent avant
 * d'en créer un autre. */
const MIN_INTERVAL_MS = 60_000;

/** Une page cachée ne rend aucune frame, et la capture y échouerait (voir `captureImage`). */
const HIDDEN_RETRY_MS = 30_000;

/**
 * Tient à jour l'aperçu du projet à partir de la carte affichée.
 *
 * Pendant la session et non en quittant l'éditeur : à la fermeture, le conteneur de la carte
 * disparaît avant que la capture et son envoi aboutissent, et un onglet qu'on ferme ne rend plus de
 * frame.
 *
 * Deux clients qui éditent le même projet captureront tous les deux, à quelques secondes d'écart et
 * avec le même contenu — redite acceptée : filtrer sur l'auteur de la modification prive de capture
 * les projets modifiés en dernier par quelqu'un d'autre.
 */
export default function useProjectThumbnail(instance: TokaMapInstance | null) {
	React.useEffect(() => {
		if (!instance) return;

		let timer: ReturnType<typeof setTimeout> | undefined;
		let lastCaptureAt = 0;
		let stopped = false;

		const schedule = (delay: number) => {
			clearTimeout(timer);
			timer = setTimeout(() => void run(), delay);
		};

		const run = async () => {
			if (stopped) return;
			if (document.hidden) return schedule(HIDDEN_RETRY_MS);

			// Un aperçu vide vaut moins que le cadre « aperçu à venir » de l'accueil.
			if (!instance.getAllShapes().length) return;

			const projectEditor = currentProjectEditorStore.get;
			const project = projectEditor.project;

			if (!project.id) return;

			// `imageOf` rend la liste telle qu'elle est et ne fait que programmer son chargement : lue
			// trop tôt, elle est vide, et on écraserait l'image de l'utilisateur en croyant qu'il n'y en
			// a pas.
			await fileStore.fetchForModel(project);

			if (stopped) return;

			const image = projectImage(project);

			if (image && !isAutoThumbnail(image)) return;

			const modifiedAt = projectEditor.lastModification?.at;

			// Aucune modification connue : rien ne dit que l'aperçu en place soit en retard.
			if (image && !modifiedAt) return;

			// `attributes.updatedAt` et non `updatedAt` : le getter de `TimestampAble` retombe sur
			// l'instant présent quand l'attribut est absent, et tout aperçu passerait pour le plus récent.
			if (image?.attributes.updatedAt && modifiedAt && image.updatedAt.toDate() >= modifiedAt) return;

			const sinceLastCapture = Date.now() - lastCaptureAt;

			if (lastCaptureAt && sinceLastCapture < MIN_INTERVAL_MS) return schedule(MIN_INTERVAL_MS - sinceLastCapture);

			lastCaptureAt = Date.now();

			try {
				await captureProjectThumbnail(project, instance);
			} catch (err) {
				// Jamais remonté à l'écran : un aperçu manqué n'est pas un incident pour qui dessine.
				console.error('Aperçu du projet', err);
			}
		};

		schedule(INITIAL_DELAY_MS);

		// Y compris les dates que l'ouverture met un moment à charger
		// (`ProjectEditorStore._loadLastModification`) : sans elles, la vérification de l'ouverture
		// s'abstient faute de date à comparer, et c'est ici qu'elle retrouve sa seconde chance.
		const dispose = reaction(
			() => currentProjectEditorStore.get.lastModification,
			modification => {
				if (!modification) return;

				// Une salve d'édition mérite qu'on attende sa fin ; des dates qui arrivent, non.
				schedule(lastCaptureAt ? QUIET_DELAY_MS : INITIAL_DELAY_MS);
			},
		);

		return () => {
			stopped = true;
			clearTimeout(timer);
			dispose();
		};
	}, [instance]);
}
